"use client";

import type { AxiosInstance } from "axios";
import {
  getAccessToken,
  isTokenExpired,
  refreshAccessToken,
  clearTokens,
} from "@/lib/httpClient";
import { getUserInfo } from "@providers/auth-provider/auth-provider.client";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3200/api";

const PERMISSIONS_ERROR_MESSAGE =
  "User has no access to requested resource - insufficient permissions";

/**
 * Installs the auth-token guard on an axios instance so calls made through a refine
 * data provider follow the same refresh-then-logout flow as `httpClient.ts`:
 *
 *  - Request: if the access token is expired, refresh it before sending the request.
 *  - Response (401): ping `/oauth/ping` to tell an expired token (-> refresh + retry the
 *    original request once) apart from a permissions issue (-> surface as an authorization
 *    error so the session is kept). If the refresh itself fails, tokens are cleared so
 *    refine's authProvider.onError logs the user out and redirects to /login.
 *
 * Token refresh is deduped inside `refreshAccessToken`, so concurrent guarded calls
 * share a single refresh.
 */
export const installAuthGuard = (instance: AxiosInstance) => {
  // Request interceptor proactively refresh an expired token before the call.
  instance.interceptors.request.use(async (config) => {
    let token = getAccessToken();
    const username = getUserInfo()?.user;

    if (token && isTokenExpired() && username) {
      try {
        token = await refreshAccessToken(username);
      } catch {
        // Refresh failed here, let the request go out and the 401 path handle logout.
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor on 401, distinguish auth from authz and recover when possible.
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalConfig = error?.config;
      const status = error?.response?.status;
      const token = getAccessToken();
      const username = getUserInfo()?.user;

      if (
        status === 401 &&
        token &&
        username &&
        originalConfig &&
        !(originalConfig as { _retry?: boolean })._retry
      ) {
        (originalConfig as { _retry?: boolean })._retry = true;

        try {
          // Distinguish authentication (expired token) from authorization (no perms).
          const ping = await fetch(`${API_URL}/oauth/ping`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (ping.status === 401) {
            // Token expired -> refresh and retry the original request once.
            const newToken = await refreshAccessToken(username);
            originalConfig.headers = originalConfig.headers || {};
            originalConfig.headers.Authorization = `Bearer ${newToken}`;

            try {
              return await instance(originalConfig);
            } catch (retryError: any) {
              // A 401 with a freshly refreshed token means the user lacks permissions,
              // not that the session is invalid keep them logged in.
              if (retryError?.response?.status === 401) {
                retryError.message = PERMISSIONS_ERROR_MESSAGE;
              }
              return Promise.reject(retryError);
            }
          }

          // Ping OK but the request was 401 -> user lacks permissions. Keep the session;
          // shape the error so authProvider.onError treats it as an authorization error.
          error.message = PERMISSIONS_ERROR_MESSAGE;
          return Promise.reject(error);
        } catch {
          // Refresh failed -> clear tokens so `authProvider.onError` logs the user out.
          clearTokens();
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
};
